/**
 * Spare copies of the small records this browser cannot afford to lose.
 *
 * localStorage is where progress and the chat identity actually live; this
 * file writes a second and third copy of the same bytes into IndexedDB and
 * the Cache Storage API, and reads them back when the first copy is gone.
 * It is a mirror, not a store: nothing here is the source of truth, nothing
 * here is read before the first render, and every function is safe to call
 * in a browser that refuses all three.
 *
 * Why bother, given that the three are usually cleared together. Because
 * "usually" is doing a lot of work in that sentence. localStorage is the
 * one a site clears by hand, the one a quota eviction takes first, the one
 * a "clear recent history" sweep in some browsers empties on its own, and
 * the one Safari's storage policy expires after seven days without a visit.
 * A learner who comes back to a level map wiped by any of those has lost
 * something real. The browser's own "delete everything for this site" does
 * take all three at once, and it is meant to — see the module comment on
 * exportCode in backup.ts for the only honest answer to that one.
 *
 * The mirrors are enveloped and the primary is not. localStorage keeps the
 * exact string it has always kept, so a build with this file and a build
 * without it read each other's storage perfectly; the mirrors wrap the same
 * string with the time it was written, which is what lets a caller holding
 * two surviving copies tell which one is newer.
 *
 * Nothing here ever throws and nothing here is awaited on a path a person
 * is waiting on. Storage is absent, partitioned away or simply broken in
 * more places than it should be, and none of them is a reason for the game
 * to stop.
 */

/** One mirrored copy, as it sits in IndexedDB and in the cache. */
interface Envelope {
  v: 1;
  /** Date.now() when the copy was written. Only ever compared with another envelope's. */
  at: number;
  /**
   * The exact string the primary copy holds, or the empty string for a
   * tombstone. A real body is a JSON blob and is never empty, so the two
   * cannot be confused. See forgetMirrors for why a tombstone exists.
   */
  body: string;
}

const DB_NAME = 'engineeros';
const DB_VERSION = 1;
const STORE = 'vault';
const CACHE_NAME = 'engineeros.vault.v1';

/**
 * The cache is keyed by URL, so each record needs one. It is never fetched —
 * `cache.match` and `cache.put` are given the string directly and no request
 * ever leaves the browser — but it must still be a URL the origin could have
 * served, which is why it is a path and not an opaque name.
 */
function cacheUrl(key: string): string {
  return `/__vault/${encodeURIComponent(key)}`;
}

function envelope(raw: string): string {
  return JSON.stringify({ v: 1, at: Date.now(), body: raw } satisfies Envelope);
}

function unwrap(text: string | null | undefined): Envelope | null {
  if (!text) return null;
  try {
    const e = JSON.parse(text) as Partial<Envelope>;
    if (e && e.v === 1 && typeof e.body === 'string' && typeof e.at === 'number') return { v: 1, at: e.at, body: e.body };
  } catch {
    /* a half-written or foreign value: no copy here */
  }
  return null;
}

/* ── IndexedDB ───────────────────────────────────────────────────────────── */

/**
 * The database, opened once and kept. The promise is cached rather than the
 * connection so that concurrent callers share one open, and a failure is
 * cached as null so a browser that refuses IndexedDB is asked exactly once.
 */
let dbPromise: Promise<IDBDatabase | null> | null = null;

function openDb(): Promise<IDBDatabase | null> {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise<IDBDatabase | null>((resolve) => {
    try {
      if (typeof indexedDB === 'undefined') return resolve(null);
      const req = indexedDB.open(DB_NAME, DB_VERSION);
      req.onupgradeneeded = () => {
        const db = req.result;
        if (!db.objectStoreNames.contains(STORE)) db.createObjectStore(STORE);
      };
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => resolve(null);
      // Firefox in a private window resolves neither, so the open is given a
      // deadline. Resolving null twice is harmless; the first one wins.
      req.onblocked = () => resolve(null);
      setTimeout(() => resolve(null), 3000);
    } catch {
      resolve(null);
    }
  });
  return dbPromise;
}

function idbRun<T>(mode: IDBTransactionMode, work: (store: IDBObjectStore) => IDBRequest<T>): Promise<T | null> {
  return openDb().then(
    (db) =>
      new Promise<T | null>((resolve) => {
        if (!db) return resolve(null);
        try {
          const tx = db.transaction(STORE, mode);
          const req = work(tx.objectStore(STORE));
          req.onsuccess = () => resolve(req.result);
          req.onerror = () => resolve(null);
          tx.onabort = () => resolve(null);
        } catch {
          resolve(null);
        }
      }),
  );
}

/* ── Cache Storage ───────────────────────────────────────────────────────── */

function openCache(): Promise<Cache | null> {
  try {
    if (typeof caches === 'undefined') return Promise.resolve(null);
    return caches.open(CACHE_NAME).catch(() => null);
  } catch {
    // Accessing `caches` throws outright on an insecure origin in some browsers.
    return Promise.resolve(null);
  }
}

/* ── The three operations ────────────────────────────────────────────────── */

/**
 * Write the spare copies. Returns immediately; the writes land when they
 * land. Callers write their own primary copy first and do not wait for this
 * one, because a mirror that made a clear feel slow would be a bad trade.
 */
export function mirror(key: string, raw: string): void {
  const text = envelope(raw);
  void idbRun('readwrite', (s) => s.put(text, key));
  void openCache().then((c) => {
    if (!c) return;
    // A plain 200 with the envelope as its body. `cache.put` refuses a
    // response it considers an error, so the status is set explicitly.
    return c.put(cacheUrl(key), new Response(text, { status: 200, headers: { 'Content-Type': 'application/json' } })).catch(() => undefined);
  });
}

/**
 * Every spare copy that survived, newest first, as the raw strings the
 * primary copy would have held. The caller decides what to do with them:
 * progress merges them all, an identity takes the newest. Never rejects —
 * a browser with no mirrors returns an empty array.
 *
 * A tombstone as the newest copy empties the result. Anything still sitting
 * behind it was written before the record was deliberately forgotten, and
 * handing that back is precisely the resurrection forgetMirrors exists to
 * prevent.
 */
export async function mirroredCopies(key: string): Promise<string[]> {
  const found: Envelope[] = [];

  const fromIdb = await idbRun<string>('readonly', (s) => s.get(key) as IDBRequest<string>);
  const a = unwrap(fromIdb);
  if (a) found.push(a);

  const cache = await openCache();
  if (cache) {
    try {
      const res = await cache.match(cacheUrl(key));
      const b = unwrap(res ? await res.text() : null);
      if (b) found.push(b);
    } catch {
      /* no copy here */
    }
  }

  found.sort((x, y) => y.at - x.at);
  if (found[0]?.body === '') return [];
  return found.map((e) => e.body).filter((body) => body !== '');
}

/**
 * Forget the spare copies. Awaited by the one caller that must not race: an
 * in-app reset that cleared localStorage and left a mirror behind would be
 * undone by the next load, and a learner who asked the game to forget their
 * progress and watched it come back would be right never to trust it again.
 *
 * Which is why this overwrites rather than deletes. A tombstone is newer than
 * every copy it replaces, so a store that still holds an old body is read as
 * "deliberately forgotten" rather than as progress.
 *
 * It deliberately does not delete afterwards, though deleting would leave a
 * tidier store. Deleting is the operation that can make things worse: one
 * store refusing both its tombstone write and its delete, while the other
 * store's delete succeeds, removes the only tombstone and leaves the stale
 * body behind it as the newest copy — the exact resurrection this function
 * exists to prevent, reached by trying to be tidy. A tombstone left lying
 * around costs forty bytes and is overwritten by the next real save; a
 * resurrected record costs a learner their trust. So: only ever overwrite.
 */
export async function forgetMirrors(key: string): Promise<void> {
  const stone = JSON.stringify({ v: 1, at: Date.now(), body: '' } satisfies Envelope);
  await idbRun('readwrite', (s) => s.put(stone, key));
  const cache = await openCache();
  if (cache) {
    try {
      await cache.put(cacheUrl(key), new Response(stone, { status: 200, headers: { 'Content-Type': 'application/json' } }));
    } catch {
      /* this store keeps whatever it had; the other one's tombstone outranks it */
    }
  }
}

/* ── Persistence ─────────────────────────────────────────────────────────── */

/**
 * Ask the browser not to evict this origin's storage when the disk gets
 * tight. Chromium decides silently from how much the person uses the site;
 * Firefox asks them. Which is why this is not called on load: a permission
 * prompt on a page someone has just opened is noise, and the same prompt
 * after they have cleared their first level is a question with an obvious
 * answer. Called more than once it is cheap — a granted origin short-circuits
 * on `persisted()` and a denied one is not asked twice in a session.
 */
let persistAsked = false;

export async function askToPersist(): Promise<boolean> {
  if (persistAsked) return false;
  persistAsked = true;
  try {
    if (!navigator.storage?.persist) return false;
    if (await navigator.storage.persisted()) return true;
    return await navigator.storage.persist();
  } catch {
    return false;
  }
}
